import React from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  AppBar,
  Checkbox,
  Grid,
  Paper,
  Toolbar,
  Typography,
} from '@material-ui/core';
import RowDiningRoom from './TimeTableRows/RowDiningRoom';
import RowDriveThru from './TimeTableRows/RowDriveThru';
import RowDeliveryTime from './TimeTableRows/RowDeliveryTime';
import RowCatering from './TimeTableRows/RowCatering';
import RowCurbside from './TimeTableRows/RowCurbside';
import RowArrival from './TimeTableRows/RowArrival';
import RowDeparture from './TimeTableRows/RowDeparture';
import RowShowTime from './TimeTableRows/RowShowTime';
import TimeTableFooter from './TimeTableFooter';
import {
  setCloseToAll,
  setOpen24HoursToAll,
} from './../../redux/actions/newOperationHoursActions';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    appBar: {
      backgroundColor: 'rgb(138, 138, 138)',
    },
    paper: {
      padding: theme.spacing(2),
      textAlign: 'left',
      color: theme.palette.text.secondary,
      height: '60vh',
      overflow: 'auto',
    },
    desktopCheckboxControl: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      alignItems: 'flex-start',
    },
  }),
);

export default function ({ isRange }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch: any = useDispatch();
  const { hasEditHoursOfOperation, hasViewMenuItemOperationHours } = useSelector(
    (state: RootStateOrAny) => state.UserReducer.thisUser
  );
  const brand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );
  const { Region, RestaurantId } = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.entity,
  );

  const { selectedStartDate, isCalculated, open24Hours, closeToAll, drIsClosed, dtIsClosed, drIsOpened24Hours, dtIsOpened24Hours } =
    useSelector((state: RootStateOrAny) => state.NewOperationHoursReducer);

  const disableAll = ((RestaurantId ? hasEditHoursOfOperation : hasViewMenuItemOperationHours) && selectedStartDate)
    ? isRange
      ? selectedStartDate.isBefore(moment().add(1, 'days').startOf('day'))
      : selectedStartDate.isBefore(moment().startOf('day'))
    : true;

  const restaurantServices = useSelector(
    (state: RootStateOrAny) => state.ServicesReducer.entities,
  );

  const curbsideService = restaurantServices.find(elem => elem.code === "curbsidePickup");
  const cateringService = restaurantServices.find(elem => elem.code === "catering");
  const driveThruService = restaurantServices.find(elem => elem.code === "driveThru");
  const setCheckbox24hours = value => {
    dispatch(setOpen24HoursToAll(value, isCalculated));
  };

  const setCheckboxClose = value => {
    dispatch(setCloseToAll(value));
  };

  return (
    <Grid item xs={12} lg={6}>
      <AppBar position="static" className={classes.appBar}>
        <Toolbar variant="dense">
          <Grid container lg={12}>
            <Grid item lg={4} xs={4} spacing={5} style={{ margin: 'auto' }}>
              <Typography>{t('operationHours.timetable')}</Typography>
            </Grid>
            <Grid item lg={2} xs={1} spacing={5} style={{ marginLeft: 0 }}>
              <Grid container lg={12} spacing={5} direction="row">
                <Grid item lg={1} xs={1} className={classes.desktopCheckboxControl}>
                  <Typography style={{ padding: 0 }}>
                    <small>{t('app.common.twentyFourHour')}</small>
                  </Typography>
                  <Checkbox
                    style={{ color: 'white', padding: 0 }}
                    disabled={disableAll}
                    checked={((driveThruService && !driveThruService.value) && drIsOpened24Hours === 'Y' && dtIsOpened24Hours === 'N')? true : open24Hours}
                    onChange={e => setCheckbox24hours(e.target.checked)}
                  />
                </Grid>
                <Grid item lg={1} xs={1} className={classes.desktopCheckboxControl}>
                  <Typography style={{ padding: 0 }}>
                    <small>{t('operationHours.closed')}</small>
                  </Typography>
                  <Checkbox
                    style={{ color: 'white', padding: 0 }}
                    disabled={disableAll}
                    checked={((driveThruService && !driveThruService.value) && drIsClosed === 'Y' && dtIsClosed === 'N')? true : closeToAll}
                    onChange={e => setCheckboxClose(e.target.checked)}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item lg={1} xs={3}></Grid>
            <Grid item lg={5} xs={4}></Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <Paper className={classes.paper}>
        <RowArrival disableAll={disableAll} />
        {brand === 'TH' && (Region === 'NA' || !RestaurantId) && (<RowDeparture disableAll={disableAll} />)}
        <RowDiningRoom disableAll={disableAll} />
        {driveThruService && driveThruService.value ? <RowDriveThru disableAll={disableAll} /> : null}
        <RowDeliveryTime disableAll={disableAll} />
        {cateringService && cateringService.value ? <RowCatering disableAll={disableAll} /> : null}
        {curbsideService && curbsideService.value ? <RowCurbside disableAll={disableAll} /> : null}
        <React.Fragment>
          <RowShowTime
            label={t('operationHours.breakfastTime')}
            open="breakfastTimeOpen"
            close="breakfastTimeClose"
          />
          <RowShowTime
            label={t('operationHours.lunchTime')}
            open="lunchTimeOpen"
            close="lunchTimeClose"
          />
          <RowShowTime
            label={t('operationHours.snackTime')}
            open="snackTimeOpen"
            close="snackTimeClose"
          />
          <RowShowTime
            label={t('operationHours.dinnerTime')}
            open="dinnerTimeOpen"
            close="dinnerTimeClose"
          />
          <RowShowTime
            label={t('operationHours.lateNight')}
            open="lateNightTimeOpen"
            close="lateNightTimeClose"
          />
        </React.Fragment>
      </Paper>
      <TimeTableFooter />
    </Grid>
  );
}
